/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: cdrSettingsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for CDR Settings.
    Centralizes all API calls related to CDR Settings.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  GetCdrSettingsResponse,
  UpdateCdrSettingsResponse,
  UpdateCdrFieldsResponse,
  CdrPreviewResponse,
} from '../types';

/**
 * Payload for updating CDR settings
 */
export interface UpdateCdrSettingsPayload {
  settings?: Record<string, unknown>;
  active?: boolean;
}

/**
 * Payload for updating CDR fields
 */
export interface UpdateCdrFieldsPayload {
  fields?: Array<{
    id?: number;
    varName?: string;
    aliasName?: string | null;
    quote?: boolean;
    enabled?: boolean;
    sortOrder?: number;
  }>;
}

/**
 * Fetch CDR settings and fields
 */
export async function fetchCdrSettings(): Promise<GetCdrSettingsResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/cdr-settings'),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch CDR settings');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch CDR settings');
  }

  return result;
}

/**
 * Update CDR settings
 */
export async function updateCdrSettings(
  payload: UpdateCdrSettingsPayload
): Promise<UpdateCdrSettingsResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/cdr-settings'),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update CDR settings');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update CDR settings');
  }

  return result;
}

/**
 * Update CDR fields
 */
export async function updateCdrFields(
  payload: UpdateCdrFieldsPayload
): Promise<UpdateCdrFieldsResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/cdr-settings/fields'),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update CDR fields');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update CDR fields');
  }

  return result;
}

/**
 * Preview CDR XML
 */
export async function previewCdrXml(): Promise<CdrPreviewResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/cdr-settings/preview'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({}),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to generate preview');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to generate preview');
  }

  return result;
}

/**
 * Apply CDR settings
 */
export async function applyCdrSettings(): Promise<CdrPreviewResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/cdr-settings/apply'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({}),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to apply settings');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to apply settings');
  }

  return result;
}

